import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { Crown, Mail, Loader2, X, Gift, Check, Star, CreditCard, User, LogIn, Info, MessageCircle, Calendar, Sparkles, Heart, BookOpen, Map } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import AuthModal from './AuthModal';
import BirthDateInfoModal, { hasSeenBirthDateInfo, markBirthDateInfoSeen } from './BirthDateInfoModal';
import { Link } from 'react-router-dom';
import API_BASE_URL from '@/utils/apiUrl';

const BACKEND_URL = API_BASE_URL;

export default function VIPModal({ isOpen, onClose, onSuccess, userData, initialPlan }) {
  const { user, isAuthenticated } = useAuth();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(initialPlan || 'yearly');
  const [selectedPayment, setSelectedPayment] = useState('stripe');
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showBirthDateGuide, setShowBirthDateGuide] = useState(false);
  const [showConsultationInfo, setShowConsultationInfo] = useState(false);
  const [showCompatibilityInfo, setShowCompatibilityInfo] = useState(false);

  // Update selected plan when initialPlan changes
  useEffect(() => {
    if (initialPlan) {
      setSelectedPlan(initialPlan);
    }
  }, [initialPlan]);

  // Pre-fill email if user is authenticated
  useEffect(() => {
    if (user?.email) {
      setEmail(user.email);
    }
  }, [user]);

  // Reset info states when modal closes
  useEffect(() => {
    if (!isOpen) {
      setShowConsultationInfo(false);
      setShowCompatibilityInfo(false);
    }
  }, [isOpen]);

  // Close consultation info when clicking outside (only when modal is open)
  useEffect(() => {
    if (!isOpen || !showConsultationInfo) return;
    
    const handleClickOutside = (event) => {
      // Don't close if clicking inside the consultation info container
      if (event.target.closest('.consultation-info-container')) {
        return;
      }
      setShowConsultationInfo(false);
    };

    // Use setTimeout to avoid capturing the same click that opened the info
    const timeoutId = setTimeout(() => {
      document.addEventListener('click', handleClickOutside);
    }, 100);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('click', handleClickOutside);
    };
  }, [isOpen, showConsultationInfo]);

  if (!isOpen) return null;

  // Get user data from authenticated user or localStorage
  const getUserDataForPayment = () => {
    if (user) {
      return {
        firstName: user.firstName,
        lastName: user.lastName,
        day: user.day,
        month: user.month,
        year: user.year,
        lifePath: user.lifePath
      };
    }
    
    // Fallback to localStorage data
    const storedData = localStorage.getItem('numerologyData');
    if (storedData) {
      return JSON.parse(storedData);
    }
    
    return userData || {};
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Check if user is authenticated
    if (!isAuthenticated) {
      toast.error('Prašome prisijungti arba užsiregistruoti prieš mokėjimą');
      setShowAuthModal(true);
      return;
    }
    
    if (!email) {
      toast.error('Įveskite el. pašto adresą');
      return;
    }
    
    // Validate email format
    if (!email.includes('@') || !email.includes('.')) {
      toast.error('Įveskite teisingą el. pašto adresą');
      return;
    }

    setLoading(true);
    toast.info('Ruošiamas mokėjimas...');
    
    // Map plan names
    const planMapping = {
      'monthly': 'vip_monthly',
      'yearly': 'vip_yearly',
      'yearly_premium': 'vip_premium'
    };
    
    try {
      const originUrl = window.location.origin;
      const paymentUserData = getUserDataForPayment();
      
      if (selectedPayment === 'paypal') {
        // Create PayPal checkout
        const response = await axios.post(`${BACKEND_URL}/api/paypal/create-paypal-checkout`, {
          plan_id: planMapping[selectedPlan],
          email,
          firstName: paymentUserData.firstName || ''
        });

        if (response.data.success && response.data.approval_url) {
          const paypalUrl = response.data.approval_url;
          toast.success('Nukreipiama į PayPal...');
          
          // Redirect after short delay to show toast
          setTimeout(() => {
            window.location.replace(paypalUrl);
          }, 800);
        } else {
          setLoading(false);
          toast.error(response.data.detail || 'Nepavyko sukurti PayPal mokėjimo');
        }
      } else {
        // Create Stripe checkout session
        const response = await axios.post(`${BACKEND_URL}/api/payments/create-checkout`, {
          plan: selectedPlan,
          email,
          firstName: paymentUserData.firstName || '',
          lifePath: paymentUserData.lifePath || 0,
          origin_url: originUrl,
          userData: paymentUserData
        });

        if (response.data.success && response.data.url) {
          const stripeUrl = response.data.url;
          toast.success('Nukreipiama į Stripe...');
          
          // Redirect after short delay to show toast
          setTimeout(() => {
            window.location.replace(stripeUrl);
          }, 800);
        } else {
          setLoading(false);
          toast.error(response.data.error || 'Nepavyko sukurti mokėjimo sesijos');
        }
      }
    } catch (error) {
      console.error('Checkout error:', error);
      setLoading(false);
      
      if (error.response?.status === 401) {
        toast.error('Sesija pasibaigė. Prašome prisijungti iš naujo.');
        setShowAuthModal(true);
      } else if (error.response?.data?.error || error.response?.data?.detail) {
        toast.error(error.response.data.error || error.response.data.detail);
      } else {
        toast.error('Klaida kuriant mokėjimą. Bandykite dar kartą.');
      }
    }
  };

  const handleAuthSuccess = () => {
    setShowAuthModal(false);
    // Email will be auto-filled via useEffect when user state updates
    // Show success message
    toast.success('Sėkmingai prisijungta! Dabar galite atlikti mokėjimą.');
  };

  // Handle registration button click - show birth date guide first
  const handleRegisterClick = () => {
    // Show birth date guide only if user hasn't seen it yet
    if (!hasSeenBirthDateInfo()) {
      setShowBirthDateGuide(true);
    } else {
      // Go directly to auth modal if already seen
      setShowAuthModal(true);
    }
  };

  // After birth date guide is closed, show auth modal
  const handleBirthDateGuideClose = () => {
    setShowBirthDateGuide(false);
    markBirthDateInfoSeen(); // Mark as seen
    setShowAuthModal(true);
  };

  return (
    <>
      <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
        <Card className="relative w-full max-w-lg bg-gradient-to-br from-violet-950 via-indigo-950 to-slate-950 border-2 border-amber-500/30 shadow-glow p-6 pb-16 max-h-[90vh] overflow-y-auto">
          {/* Close button */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-muted-foreground hover:text-foreground"
          >
            <X className="w-5 h-5" />
          </button>

          {/* Header */}
          <div className="text-center mb-6">
            <div className="w-16 h-16 bg-amber-500/20 rounded-full flex items-center justify-center mx-auto mb-4 border-2 border-amber-500/50">
              <Crown className="w-8 h-8 text-amber-400" />
            </div>
            <h2 className="font-display text-2xl text-amber-400 mb-2">
              Atverk pilną savo vidinį žemėlapį
            </h2>
            <p className="text-sm text-foreground/70">
              Pasirink sau tinkamiausią planą
            </p>
          </div>

          {/* Auth Status Banner */}
          {!isAuthenticated && (
            <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 mb-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <User className="w-4 h-4 text-amber-400" />
                  <span className="text-sm text-foreground/80">Norint įsigyti VIP, reikia prisijungti</span>
                </div>
                <Button
                  type="button"
                  size="sm"
                  onClick={() => setShowAuthModal(true)}
                  className="bg-amber-500 hover:bg-amber-600 text-black text-xs"
                >
                  <LogIn className="w-3 h-3 mr-1" />
                  Prisijungti
                </Button>
              </div>
            </div>
          )}

          {/* Plan Selection - 3 Plans */}
          <div className="space-y-3 mb-6">
            {/* Row 1: Monthly and Yearly side by side */}
            <div className="grid grid-cols-2 gap-3">
              {/* Monthly Plan */}
              <button
                type="button"
                onClick={() => setSelectedPlan('monthly')}
                className={`relative p-4 rounded-lg border-2 transition-all duration-300 text-left ${
                  selectedPlan === 'monthly'
                    ? 'border-amber-500/70 bg-amber-500/10'
                    : 'border-border/50 bg-secondary/10 hover:border-amber-500/30'
                }`}
              >
                <div className="flex items-center gap-2 mb-2">
                  <Calendar className="w-4 h-4 text-amber-400" />
                  <span className="text-sm font-semibold text-foreground">Mėnesinis</span>
                </div>
                <div className="text-2xl font-bold text-amber-400">€2.99</div>
                <div className="text-xs text-muted-foreground">per mėnesį</div>
                {selectedPlan === 'monthly' && (
                  <div className="absolute top-2 right-2">
                    <Check className="w-5 h-5 text-amber-400" />
                  </div>
                )}
              </button>

              {/* Yearly Plan */}
              <button
                type="button"
                onClick={() => setSelectedPlan('yearly')}
                className={`relative p-4 rounded-lg border-2 transition-all duration-300 text-left ${
                  selectedPlan === 'yearly'
                    ? 'border-green-500/70 bg-green-500/10 shadow-lg shadow-green-500/20'
                    : 'border-green-500/30 bg-green-500/5 hover:border-green-500/50'
                }`}
              >
                {/* Best Value Badge */}
                <div className="absolute -top-2 -right-2 bg-green-500 text-black text-[10px] font-bold px-2 py-0.5 rounded-full flex items-center gap-1">
                  <Star className="w-3 h-3" />
                  POPULIARIAUSIAS
                </div>
                
                <div className="flex items-center gap-2 mb-2">
                  <Gift className="w-4 h-4 text-green-400" />
                  <span className="text-sm font-semibold text-foreground">Metinis</span>
                </div>
                <div className="text-2xl font-bold text-green-400">€25</div>
                <div className="text-xs text-muted-foreground">per metus</div>
                {selectedPlan === 'yearly' && (
                  <div className="absolute top-2 right-2">
                    <Check className="w-5 h-5 text-green-400" />
                  </div>
                )}
              </button>
            </div>

            {/* Row 2: Premium Plan with Consultations - Full Width */}
            <div
              role="button"
              tabIndex={0}
              onClick={() => setSelectedPlan('yearly_premium')}
              onKeyDown={(e) => e.key === 'Enter' && setSelectedPlan('yearly_premium')}
              className={`relative w-full p-4 rounded-lg border-2 transition-all duration-300 text-left cursor-pointer ${
                selectedPlan === 'yearly_premium'
                  ? 'border-purple-500/70 bg-purple-500/10 shadow-lg shadow-purple-500/20'
                  : 'border-purple-500/30 bg-purple-500/5 hover:border-purple-500/50'
              }`}
            >
              {/* Premium Badge */}
              <div className="absolute -top-2 left-4 bg-purple-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full flex items-center gap-1">
                <MessageCircle className="w-3 h-3" />
                ASMENINIS RYŠYS
              </div>
              
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2 mt-1">
                    <Crown className="w-4 h-4 text-purple-400" />
                    <span className="text-sm font-semibold text-foreground">VIP Metinis + asmeninis ryšys</span>
                    {/* Info icon */}
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowConsultationInfo(!showConsultationInfo);
                      }}
                      className="consultation-info-container text-purple-400 hover:text-purple-300"
                    >
                      <Info className="w-4 h-4" />
                    </button>
                  </div>
                  <div className="text-2xl font-bold text-purple-400">€65</div>
                  <div className="text-xs text-muted-foreground">per metus</div>
                  <div className="text-[10px] text-purple-400 mt-1">6 asmeninės konsultacijos per metus</div>
                  <div className="text-[9px] text-foreground/60 mt-0.5">Metinis asmeninis palaikymas, pritaikomas pagal poreikį</div>
                </div>
                {selectedPlan === 'yearly_premium' && (
                  <div className="mt-2">
                    <Check className="w-5 h-5 text-purple-400" />
                  </div>
                )}
              </div>

              {/* Expanded info when plan is selected or info clicked - MERGED into single block */}
              {(selectedPlan === 'yearly_premium' || showConsultationInfo) && (
                <div className="mt-3 pt-3 border-t border-purple-500/30 text-xs text-foreground/80">
                  {/* Close button */}
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowConsultationInfo(false);
                      if (selectedPlan === 'yearly_premium') {
                        setSelectedPlan('yearly');
                      }
                    }}
                    className="absolute top-2 right-2 text-purple-400 hover:text-purple-300 p-1 rounded-full hover:bg-purple-500/20"
                  >
                    <X className="w-4 h-4" />
                  </button>
                  
                  <p className="font-semibold text-purple-300 mb-2">Kaip veikia asmeninės konsultacijos?</p>
                  <div className="space-y-1 mb-3 text-purple-200">
                    <p>• Visa VIP metinio plano prieiga</p>
                    <p>• 6 asmeninės konsultacijos per metus</p>
                    <p>• Konsultacijos naudojamos lanksčiai, be fiksuoto grafiko</p>
                    <p>• Iki 5 klausimų kiekvienoje konsultacijoje</p>
                    <p>• Raštu arba balsu (pagal susitarimą)</p>
                  </div>
                  <div className="bg-purple-500/20 rounded p-2 border border-purple-400/30">
                    <p className="text-purple-200 leading-relaxed text-[10px]">
                      Konsultacijos gali būti išnaudotos bet kuriuo metų laikotarpiu. Jos derinamos individualiai ir atsakymai rengiami jungiant numerologiją, astrologinius ciklus ir kontekstinį vertinimą.
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Full Plan Benefits - Detailed list when plan selected */}
          {selectedPlan && (
            <div className="bg-secondary/20 rounded-lg p-4 mb-4 border border-border/30">
              <div className="flex items-center gap-2 mb-3">
                <Sparkles className="w-4 h-4 text-amber-400" />
                <span className="text-sm font-semibold text-amber-400">Ką gausite:</span>
              </div>
              
              {selectedPlan === 'monthly' && (
                <ul className="space-y-2 text-sm text-foreground/80">
                  <li className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-green-400 flex-shrink-0 mt-0.5" />
                    <span>Asmeninė kasdienė dienos energijų prognozė el. paštu <span className="text-foreground/60 text-xs">(siunčiama vakare kitai dienai)</span></span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-green-400 flex-shrink-0 mt-0.5" />
                    <span>Pilna Sielos Matricos analizė (Psichomatrica) <span className="text-foreground/60 text-xs">– 8 papildomi langeliai ir 8 linijų analizė</span></span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-green-400 flex-shrink-0 mt-0.5" />
                    <span>Savaitės energijų srautas ir asmeninės gairės</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-green-400 flex-shrink-0 mt-0.5" />
                    <span>Mėnesio energijų paveikslas ir energetinė tema</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-green-400 flex-shrink-0 mt-0.5" />
                    <span>Savaitinis „Vidinės Ašies" ciklas su dinaminiais savianalizės klausimais</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-green-400 flex-shrink-0 mt-0.5" />
                    <span>Didesnė „Mano rato" talpa: <span className="text-amber-400 font-medium">iki 12 unikalių asmenų</span></span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-green-400 flex-shrink-0 mt-0.5" />
                    <span>Tavo Sėkmės Skrynia <span className="text-foreground/60 text-xs">(kasdien naujos žinutės)</span></span>
                  </li>
                </ul>
              )}
              
              {selectedPlan === 'yearly' && (
                <ul className="space-y-2 text-sm text-foreground/80">
                  <li className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-green-400 flex-shrink-0 mt-0.5" />
                    <span>Viskas, kas yra Mėnesiniame VIP</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-green-400 flex-shrink-0 mt-0.5" />
                    <span>12 mėnesių tendencijų žemėlapis</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-green-400 flex-shrink-0 mt-0.5" />
                    <span>Neriboti skaičiavimai "Mano rate"</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-green-400 flex-shrink-0 mt-0.5" />
                    <span>VIP prieiga prie „Sėkmės skrynios"</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-green-400 flex-shrink-0 mt-0.5" />
                    <div className="flex items-center gap-1">
                      <span>Išplėsta suderinamumo analizė</span>
                      <button
                        type="button"
                        onClick={() => setShowCompatibilityInfo(!showCompatibilityInfo)}
                        className="text-amber-400/70 hover:text-amber-400 ml-1"
                      >
                        <Info className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </li>
                  <li className="flex items-start gap-2">
                    <Gift className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
                    <span className="text-amber-400 font-medium">DOVANA: Asmeninė PDF Sielos Knyga <span className="text-foreground/60 text-xs font-normal">(išsiunčiama el. paštu per 2 savaites)</span></span>
                  </li>
                </ul>
              )}
              
              {/* DOVANA info box for yearly plan */}
              {selectedPlan === 'yearly' && (
                <div className="mt-4 p-4 rounded-lg bg-gradient-to-br from-amber-500/10 to-amber-600/10 border border-amber-500/30">
                  <div className="flex items-start gap-3">
                    <Gift className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <h4 className="text-amber-400 font-semibold text-sm mb-2">🎁 DOVANA: Sielos žemėlapio archyvas</h4>
                      <p className="text-foreground/80 text-xs leading-relaxed mb-3">
                        Tai giliausia tavo asmenybės ir likimo matrica. Tavo asmeninė Sielos Knyga, suformuota į vieną PDF dokumentą, kuriame skaičiai atskleidžia tavo prigimtį, stiprybes ir šio gyvenimo kelio užduotis.
                      </p>
                      <div className="bg-amber-500/15 rounded p-3 border border-amber-400/40">
                        <p className="text-amber-300 text-xs leading-relaxed flex items-start gap-2">
                          <Info className="w-4 h-4 flex-shrink-0 mt-0.5 text-amber-400" />
                          <span>
                            <strong className="text-amber-400">Asmeninė PDF Sielos Knyga šiuo metu ruošiama.</strong><br />
                            Visa tavo asmeninė informacija ir analizė jau pilnai prieinama šioje erdvėje be jokių apribojimų.<br />
                            PDF bus išsiųsta automatiškai el. paštu per artimiausias 2 savaites – be papildomo mokesčio.
                          </span>
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              
              {selectedPlan === 'yearly_premium' && (
                <ul className="space-y-2 text-sm text-foreground/80">
                  <li className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-green-400 flex-shrink-0 mt-0.5" />
                    <span>Viskas, kas yra Metiniame VIP</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Star className="w-4 h-4 text-purple-400 flex-shrink-0 mt-0.5" />
                    <span className="text-purple-400 font-medium">6 asmeninės konsultacijos</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-green-400 flex-shrink-0 mt-0.5" />
                    <span>Prioritetinis atsakymas į klausimus</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-green-400 flex-shrink-0 mt-0.5" />
                    <span>Neriboti skaičiavimai ir grupės "Mano rate"</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-green-400 flex-shrink-0 mt-0.5" />
                    <span>VIP prieiga prie „Sėkmės skrynios" ir DOVANA: Asmeninė PDF Sielos Knyga <span className="text-foreground/60 text-xs">(išsiunčiama el. paštu per 2 savaites)</span></span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-green-400 flex-shrink-0 mt-0.5" />
                    <div className="flex items-center gap-1">
                      <span>Išplėsta suderinamumo analizė</span>
                      <button
                        type="button"
                        onClick={() => setShowCompatibilityInfo(!showCompatibilityInfo)}
                        className="text-amber-400/70 hover:text-amber-400 ml-1"
                      >
                        <Info className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </li>
                </ul>
              )}
              
              {/* Compatibility Info Tooltip */}
              {showCompatibilityInfo && (
                <div className="mt-3 p-3 bg-amber-500/10 rounded-lg border border-amber-500/30 text-xs text-amber-200/90">
                  <div className="flex items-start gap-2">
                    <Info className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium text-amber-400 mb-1">Būtina žinoti apie suderinamumą:</p>
                      <p>Būtina žinoti partnerio duomenis ir jūsų santykių padėtį: draugai, susituokę, gyvenate kartu, išsiskyrę ir t.t. Taip pat nurodykite aspektą, kuriuo norite būti tikrinami – pvz., noriu skirtis, noriu kurti šeimą ir pan.</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="text-sm font-medium text-foreground mb-2 block">
                El. pašto adresas (mokėjimui)
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="jusu@email.lt"
                  className="pl-10 bg-secondary/20 border-amber-500/30 focus:border-amber-500"
                  required
                  data-testid="payment-email-input"
                />
              </div>
              {isAuthenticated && user?.email && email !== user.email && (
                <p className="text-xs text-amber-400 mt-1 flex items-center gap-1">
                  ⚠️ Mokėjimas bus atliktas kitu el. paštu nei registracijos
                </p>
              )}
              {isAuthenticated && user?.email && email === user.email && (
                <p className="text-xs text-green-400 mt-1 flex items-center gap-1">
                  <Check className="w-3 h-3" /> Prisijungta kaip {user?.firstName}
                </p>
              )}
            </div>

            {/* Payment Method Selection */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-foreground block">
                Mokėjimo būdas
              </label>
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  data-testid="payment-stripe-btn"
                  onClick={() => setSelectedPayment('stripe')}
                  className={`p-3 rounded-lg border-2 transition-all flex items-center justify-center gap-2 ${
                    selectedPayment === 'stripe'
                      ? 'border-amber-500/70 bg-amber-500/10'
                      : 'border-border/50 bg-secondary/10 hover:border-amber-500/30'
                  }`}
                >
                  <CreditCard className="w-4 h-4" />
                  <span className="text-sm">Kortelė</span>
                </button>
                <button
                  type="button"
                  data-testid="payment-paypal-btn"
                  onClick={() => setSelectedPayment('paypal')}
                  className={`p-3 rounded-lg border-2 transition-all flex items-center justify-center gap-2 ${
                    selectedPayment === 'paypal'
                      ? 'border-blue-500/70 bg-blue-500/10'
                      : 'border-border/50 bg-secondary/10 hover:border-blue-500/30'
                  }`}
                >
                  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M7.076 21.337H2.47a.641.641 0 0 1-.633-.74L4.944.901C5.026.382 5.474 0 5.998 0h7.46c2.57 0 4.578.543 5.69 1.81 1.01 1.15 1.304 2.42 1.012 4.287-.023.143-.047.288-.077.437-.983 5.05-4.349 6.797-8.647 6.797h-2.19a.65.65 0 0 0-.64.546L7.076 21.337z"/>
                  </svg>
                  <span className="text-sm">PayPal</span>
                </button>
              </div>
            </div>

            {/* Dynamic Button based on selected plan */}
            {!isAuthenticated ? (
              // For guest users - show register button that triggers birth date guide first
              <Button
                type="button"
                onClick={handleRegisterClick}
                className="w-full font-bold py-5 text-lg shadow-xl transition-all duration-300 bg-gradient-to-r from-amber-400 via-yellow-400 to-amber-500 hover:from-amber-500 hover:via-yellow-500 hover:to-amber-600 text-black shadow-amber-500/30 hover:shadow-amber-500/50"
              >
                <LogIn className="w-5 h-5 mr-2" />
                Registruotis ir tęsti
              </Button>
            ) : (
              // For authenticated users - show payment button
              <Button
                type="submit"
                disabled={loading}
                className={`w-full font-bold py-5 text-lg shadow-xl transition-all duration-300 disabled:opacity-50 disabled:animate-none ${
                  selectedPayment === 'paypal'
                    ? 'bg-gradient-to-r from-blue-400 via-blue-500 to-blue-600 hover:from-blue-500 hover:via-blue-600 hover:to-blue-700 text-white shadow-blue-500/40'
                    : selectedPlan === 'yearly_premium'
                    ? 'bg-gradient-to-r from-purple-400 via-violet-400 to-purple-500 hover:from-purple-500 hover:via-violet-500 hover:to-purple-600 text-white shadow-purple-500/40 hover:shadow-purple-500/60'
                    : selectedPlan === 'yearly'
                    ? 'bg-gradient-to-r from-green-400 via-emerald-400 to-green-500 hover:from-green-500 hover:via-emerald-500 hover:to-green-600 text-black shadow-green-500/40 hover:shadow-green-500/60 animate-pulse hover:animate-none'
                    : 'bg-gradient-to-r from-amber-400 via-yellow-400 to-amber-500 hover:from-amber-500 hover:via-yellow-500 hover:to-amber-600 text-black shadow-amber-500/30 hover:shadow-amber-500/50'
                }`}
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Apdorojama...
                  </>
                ) : (
                  <>
                    <Gift className="w-6 h-6 mr-2" />
                    Noriu matyti visą paveikslą ✨
                  </>
                )}
              </Button>
            )}
          </form>

          {/* Refund guarantee for premium plan */}
          {selectedPlan === 'yearly_premium' && (
            <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-3 mt-4 text-center">
              <p className="text-xs text-purple-400 font-semibold">
                ✓ 30 dienų grąžinimo garantija
              </p>
              <Link 
                to="/grazinimo-politika" 
                className="text-[10px] text-muted-foreground hover:text-purple-400 underline"
                onClick={onClose}
              >
                Daugiau informacijos – Prenumeratos ir grąžinimo politikoje
              </Link>
            </div>
          )}

          <p className="text-[10px] text-muted-foreground text-center mt-4">
            <CreditCard className="w-3 h-3 inline mr-1" />
            Saugus mokėjimas per Stripe. Paspaudę sutinkate su paslaugų sąlygomis.
          </p>
        </Card>
      </div>

      {/* Birth Date Guide Modal - shown before registration */}
      <BirthDateInfoModal 
        isOpen={showBirthDateGuide} 
        onClose={handleBirthDateGuideClose}
        onConfirm={handleBirthDateGuideClose}
      />

      {/* Auth Modal */}
      <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        onSuccess={handleAuthSuccess}
        defaultTab="register"
      />
    </>
  );
}
